import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import {
  Box,
  Button,
  Grid,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import VouchersAPI from 'api/faber/VouchersAPI'
import OrbitSession from 'hooks/useCookies'

// disable sentry
// import { CONSOLE_LEVELS, consoleSandbox } from '@sentry/utils'
import CSVUploadBox from './CSVUploadBox'
import { CustomStepIcon } from './CustomStepIcon'
import VouchersUploadDataGrid, {
  VoucherUploadRows
} from './VouchersUploadDataGrid'

const correctHeaders = [
  'expiryAt',
  'code',
  'currency',
  'value',
  'symbol',
  'partnerName'
]

/** Styled div that bolds content. */
const StrongText = styled.div({
  display: 'block',
  fontWeight: 500,
  fontSize: 18
})

export default function PreorderUpload() {
  const faberToken = OrbitSession.get('faberToken')
  const [uploadedFilename, setUploadedFilename] = useState<string>('')
  const [uploadReqBody, setUploadReqBody] = useState<IUploadReqBody>({
    data: []
  })
  const [uploadErr, setUploadErr] = useState<string>('')
  const [uploadStatus, setUploadStatus] = useState<string>('')
  const [rows, setRows] = useState<VoucherUploadRows[]>([])

  const [activeStep, setActiveStep] = useState(0)

  const navigate = useNavigate()

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  /** Helper function to parse CSV file. */
  function parseCsv(csvFile): VouchersCSVData[] | null {
    const csvFileRows: string[] = csvFile.split('\n')
    const values: VouchersCSVData[] = []
    const headers: string[] = []
    setActiveStep(0)
    for (let i = 0; i < csvFileRows.length; i++) {
      if (i === 0) {
        csvFileRows[i]
          .replaceAll('"', '')
          .trim()
          .split(',')
          .forEach((value) => headers.push(value))
        let err = false
        headers.forEach((header, index) => {
          if (header !== correctHeaders[index]) {
            err = true
          }
        })
        if (err) {
          setUploadStatus('ERROR')
          setUploadErr('Invalid csv format, please check your column headers')
          return null
        }
      } else if (csvFileRows[i] === '') {
        continue
      } else {
        const row = {} as VouchersCSVData
        csvFileRows[i]
          .replaceAll('"', '')
          .trim()
          .split(',')
          .forEach((value, index) => {
            row['id'] = i
            row[headers[index]] = value
          })
        values.push(row)
      }
    }
    setUploadStatus('PENDING')
    handleNext()
    return values
  }

  const onFileUpload = async (file) => {
    const fileName = file.name
    const reader = new FileReader()

    reader.onload = async (e) => {
      const text = e.target?.result
      const output = parseCsv(text)
      if (output === null) {
        return null
      }
      const values: VouchersCSVData[] = output
      const requestBody: IUploadReqBody = { data: [] }
      requestBody[`data`] = values
      setUploadReqBody(requestBody)
    }

    reader.readAsText(file)
    setUploadedFilename(fileName)
  }
  const { mutate: createVouchers } = useMutation(VouchersAPI.createVouchers, {
    onSuccess: (data) => {
      toast.success(data.data.message, {
        autoClose: 3000,
        hideProgressBar: false
      })
      setTimeout(() => {
        navigate(-1)
      }, 3000)
    },
    onError: () => {
      toast.error('Failed to upload vouchers')
    },
    onMutate: () => {
      toast.info('Uploading vouchers', {})
    }
  })
  const onConfirmUpload = async () => {
    createVouchers({
      uploadObject: uploadReqBody,
      faberToken: faberToken
    })
  }

  useEffect(() => {
    if (uploadReqBody.data.length > 0) {
      setRows(
        uploadReqBody.data.map((value: VouchersCSVData, index) => {
          return { ...value, id: String(index) }
        })
      )
    }
  }, [uploadReqBody])

  return (
    <Grid item>
      <Stepper
        orientation='vertical'
        activeStep={activeStep}
        sx={{
          '& .MuiStepContent-root': {
            borderLeft: '2px solid #E8E9FD',
            margin: '5px 0px 0px 15px'
          },
          '& .MuiStepLabel-label': {
            paddingLeft: '20px'
          },
          '& .MuiCollapse-wrapperInner': {
            paddingLeft: '24px'
          },
          '& .MuiStepConnector-line': {
            borderLeft: '2px solid #E8E9FD'
          },
          '& .MuiStepConnector-root': {
            marginLeft: '15px',
            marginBottom: '5px'
          }
        }}
      >
        <Step key='upload'>
          <StepLabel StepIconComponent={CustomStepIcon}>
            <StrongText>Upload CSV for new vouchers</StrongText>
          </StepLabel>
          <StepContent TransitionProps={{ in: true }}>
            <CSVUploadBox
              onFileUpload={onFileUpload}
              handleNext={handleNext}
              uploadedFilename={uploadedFilename}
              uploadStatus={uploadStatus}
              uploadErr={uploadErr}
            />
          </StepContent>
        </Step>
        {/* TODO: datagrid to review voucher csv upload pending upload schema */}
        <Step key='verify'>
          <StepLabel StepIconComponent={CustomStepIcon}>
            <StrongText>Check Upload Contents</StrongText>
          </StepLabel>
          <StepContent>
            {uploadedFilename && (
              <VouchersUploadDataGrid
                {...{
                  rows,
                  setRows
                }}
              />
            )}
            {uploadStatus === 'PENDING' && (
              <div>
                <Box>
                  <Typography sx={{ fontWeight: 500 }}>
                    Total rows: {rows.length}
                  </Typography>
                </Box>
                <Button
                  variant='contained'
                  onClick={() => {
                    setUploadStatus('VALID')
                    handleNext()
                  }}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Continue
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => {
                    handleBack()
                  }}
                  sx={{ mt: 1, mr: 1 }}
                >
                  Back
                </Button>
              </div>
            )}
          </StepContent>
        </Step>
        <Step key='confirm'>
          <StepLabel StepIconComponent={CustomStepIcon}>
            <StrongText>Confirm Upload</StrongText>
          </StepLabel>
          <StepContent>
            <Typography>
              File: <b>{uploadedFilename}</b>
            </Typography>
            <Button
              variant='contained'
              onClick={onConfirmUpload}
              sx={{ mt: 1, mr: 1 }}
            >
              Confirm upload
            </Button>
            <Button
              variant='outlined'
              onClick={() => {
                handleBack()
              }}
              sx={{ mt: 1, mr: 1 }}
            >
              Back
            </Button>
          </StepContent>
        </Step>
      </Stepper>
    </Grid>
  )
}

interface IUploadReqBody {
  data: VouchersCSVData[]
}

interface VouchersCSVData {
  expiryAt: string
  code: string
  currency: string
  value: number
  symbol: string
  partnerName: string
}
